import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/services/api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Search,
  ExternalLink,
  RefreshCw,
  Euro,
  Target,
  AlertCircle,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FileSearch,
  Timer,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  LayoutList,
  Columns3,
  GripVertical,
  UserCircle,
  User,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import LicitacionModal from "@/components/LicitacionModal";
import DateRangeFilter, { PRESETS_PLAZO } from "@/components/DateRangeFilter";

// Categoria config
const getCategoriaConfig = (categoria) => {
  const configs = {
    alta_prioridad: { color: "bg-green-500", textColor: "text-green-400", label: "Alta" },
    revisar: { color: "bg-yellow-500", textColor: "text-yellow-400", label: "Revisar" },
    baja_prioridad: { color: "bg-slate-500", textColor: "text-slate-400", label: "Baja" },
  };
  return configs[categoria] || configs.baja_prioridad;
};

// Estado config
const estadoConfig = {
  nueva: { icon: AlertCircle, color: "text-cyan-400", bg: "bg-cyan-500/20", label: "Nueva" },
  en_estudio: { icon: Search, color: "text-blue-400", bg: "bg-blue-500/20", label: "En Estudio" },
  viable: { icon: CheckCircle2, color: "text-green-400", bg: "bg-green-500/20", label: "Viable" },
  preparando_oferta: { icon: Clock, color: "text-purple-400", bg: "bg-purple-500/20", label: "Preparando" },
  oferta_presentada: { icon: TrendingUp, color: "text-indigo-400", bg: "bg-indigo-500/20", label: "Presentada" },
  en_evaluacion: { icon: Timer, color: "text-orange-400", bg: "bg-orange-500/20", label: "Evaluando" },
  adjudicada_srs: { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/20", label: "Adjudicada" },
  no_adjudicada: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/20", label: "No Adjudicada" },
  descartada: { icon: XCircle, color: "text-slate-400", bg: "bg-slate-500/20", label: "Descartada" },
  expirada: { icon: AlertTriangle, color: "text-slate-500", bg: "bg-slate-500/10", label: "Expirada" },
};

// Kanban columns config
const KANBAN_COLUMNS = [
  { id: "nueva", label: "Nueva", borderColor: "#22d3ee", bgClass: "bg-cyan-500/10" },
  { id: "en_estudio", label: "En Estudio", borderColor: "#3b82f6", bgClass: "bg-blue-500/10" },
  { id: "viable", label: "Viable", borderColor: "#22c55e", bgClass: "bg-green-500/10" },
  { id: "preparando_oferta", label: "Preparando", borderColor: "#a855f7", bgClass: "bg-purple-500/10" },
  { id: "oferta_presentada", label: "Presentada", borderColor: "#6366f1", bgClass: "bg-indigo-500/10" },
  { id: "en_evaluacion", label: "Evaluando", borderColor: "#f97316", bgClass: "bg-orange-500/10" },
];
const TERMINAL_STATES = ["adjudicada_srs", "no_adjudicada", "descartada", "expirada"];

// Calcular dias restantes
const getDiasRestantes = (fechaLimite) => {
  if (!fechaLimite) return null;
  const ahora = new Date();
  const limite = new Date(fechaLimite);
  const diff = Math.ceil((limite - ahora) / (1000 * 60 * 60 * 24));
  return diff;
};

const getDiasColor = (dias) => {
  if (dias === null) return "text-slate-500";
  if (dias <= 0) return "text-red-500";
  if (dias <= 7) return "text-red-400";
  if (dias <= 15) return "text-orange-400";
  if (dias <= 30) return "text-yellow-400";
  return "text-green-400";
};

const formatPilarName = (pilar) => {
  if (!pilar) return pilar;
  return pilar
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

// User colors (same as Oportunidades)
const userColors = [
  { bg: "bg-cyan-600", text: "text-cyan-400", border: "border-cyan-500" },
  { bg: "bg-purple-600", text: "text-purple-400", border: "border-purple-500" },
  { bg: "bg-orange-600", text: "text-orange-400", border: "border-orange-500" },
  { bg: "bg-green-600", text: "text-green-400", border: "border-green-500" },
  { bg: "bg-pink-600", text: "text-pink-400", border: "border-pink-500" },
  { bg: "bg-yellow-600", text: "text-yellow-400", border: "border-yellow-500" },
  { bg: "bg-red-600", text: "text-red-400", border: "border-red-500" },
  { bg: "bg-indigo-600", text: "text-indigo-400", border: "border-indigo-500" },
];

const getUserColorByName = (name, users) => {
  if (!name) return null;
  const index = users.findIndex((u) => u.name === name);
  if (index === -1) return userColors[0];
  return userColors[index % userColors.length];
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].substring(0, 2).toUpperCase();
};

export default function Licitaciones({ user }) {
  const [licitaciones, setLicitaciones] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // View mode
  const [viewMode, setViewMode] = useState("table");

  // Filters
  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("todas");
  const [categoriaFilter, setCategoriaFilter] = useState("todas");
  const [pilarFilter, setPilarFilter] = useState("todos");
  const [regionFilter, setRegionFilter] = useState("todos");
  // v4.4.33 — filtro fecha (plazo presentación por defecto)
  const [fechaRango, setFechaRango] = useState({ desde: null, hasta: null });
  const [responsableFilter, setResponsableFilter] = useState("todos");
  const [ordenFilter, setOrdenFilter] = useState("score");

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50;

  // Modal
  const [selectedLicitacion, setSelectedLicitacion] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Deep-link highlight
  const [highlightId, setHighlightId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchLicitaciones = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (estadoFilter && estadoFilter !== "todas") params.append("estado", estadoFilter);
      if (categoriaFilter && categoriaFilter !== "todas") params.append("categoria", categoriaFilter);
      if (pilarFilter && pilarFilter !== "todos") params.append("pilar", pilarFilter);
      if (responsableFilter && responsableFilter !== "todos") params.append("responsable", responsableFilter);
      if (regionFilter && regionFilter !== "todos") params.append("lugar_ejecucion", regionFilter);
      if (search) params.append("busqueda", search);
      // v4.4.33 — rango fechas (plazo presentación)
      if (fechaRango.desde) params.append("fecha_desde", fechaRango.desde);
      if (fechaRango.hasta) params.append("fecha_hasta", fechaRango.hasta);
      params.append("orden", ordenFilter);

      // Kanban: fetch all; Table: paginate
      if (viewMode === "kanban") {
        params.append("limit", "500");
      } else {
        params.append("page", String(page));
        params.append("limit", String(PAGE_SIZE));
      }

      const response = await api.get(`/licitaciones?${params.toString()}`);
      const { items, total, pages } = response.data;
      setLicitaciones(items);
      setTotalCount(total);
      setTotalPages(pages);
    } catch (error) {
      toast.error("Error al cargar licitaciones");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [estadoFilter, categoriaFilter, pilarFilter, regionFilter, responsableFilter, search, ordenFilter, fechaRango, page, viewMode]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/licitaciones/stats');
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching licitaciones stats:", error);
      toast.error("Error cargando estadísticas");
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error cargando usuarios");
    }
  };

  useEffect(() => {
    fetchStats();
    fetchUsers();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [estadoFilter, categoriaFilter, pilarFilter, responsableFilter, search, ordenFilter, viewMode]);

  useEffect(() => {
    fetchLicitaciones();
  }, [fetchLicitaciones]);

  // Deep-link: highlight row from ?open=ID
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && licitaciones.length > 0) {
      const match = licitaciones.find(
        (l) => l._id === openId || l.expediente_id === openId
      );
      if (match) {
        setHighlightId(match._id);
        setSearchParams({}, { replace: true });
        setTimeout(() => {
          const el = document.getElementById(`lic-row-${match._id}`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
        setTimeout(() => setHighlightId(null), 5000);
      } else {
        // Not on current page/filter — open modal as fallback
        const fetchAndOpen = async () => {
          try {
            const response = await api.get(`/licitaciones/${openId}`);
            setSelectedLicitacion(response.data);
            setModalOpen(true);
          } catch (error) {
            toast.error("No se pudo encontrar la licitación");
          } finally {
            setSearchParams({}, { replace: true });
          }
        };
        fetchAndOpen();
      }
    }
  }, [searchParams, licitaciones]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = (licitacion) => {
    setSelectedLicitacion(licitacion);
    setModalOpen(true);
    const id = licitacion._id || licitacion.id;
    if (id) setSearchParams({ open: id }, { replace: true });
  };

  const closeModal = () => {
    setSelectedLicitacion(null);
    setModalOpen(false);
    setSearchParams({}, { replace: true });
    fetchLicitaciones();
    fetchStats();
  };

  // Assign responsable inline
  const assignResponsable = async (e, lic, userName) => {
    e.stopPropagation();
    try {
      await api.put(
        `/licitaciones/${lic._id}`,
        { responsable: userName || "" }
      );
      setLicitaciones((prev) =>
        prev.map((l) => (l._id === lic._id ? { ...l, responsable: userName || "" } : l))
      );
      toast.success(userName ? `Asignado a ${userName}` : "Asignación eliminada");
    } catch (error) {
      toast.error("Error al asignar");
    }
  };

  // Cycle estados rapido (table view)
  const cycleEstado = async (e, lic) => {
    e.stopPropagation();
    const cycle = ["nueva", "en_estudio", "viable", "descartada"];
    const currentIndex = cycle.indexOf(lic.estado);
    const nextIndex = (currentIndex + 1) % cycle.length;
    const nextEstado = cycle[nextIndex];

    try {
      await api.put(
        `/licitaciones/${lic._id}`,
        { estado: nextEstado }
      );
      setLicitaciones((prev) =>
        prev.map((l) => (l._id === lic._id ? { ...l, estado: nextEstado } : l))
      );
      toast.success(`Estado: ${estadoConfig[nextEstado]?.label || nextEstado}`);
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  // Kanban drag & drop
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const nextEstado = destination.droppableId;

    // Optimistic update
    setLicitaciones((prev) =>
      prev.map((l) => (l._id === draggableId ? { ...l, estado: nextEstado } : l))
    );

    try {
      await api.put(
        `/licitaciones/${draggableId}`,
        { estado: nextEstado }
      );
      toast.success(`Estado: ${estadoConfig[nextEstado]?.label || nextEstado}`);
    } catch (error) {
      // Revert
      setLicitaciones((prev) =>
        prev.map((l) =>
          l._id === draggableId ? { ...l, estado: source.droppableId } : l
        )
      );
      toast.error("Error al cambiar estado");
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "-";
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const clearFilters = () => {
    setSearch("");
    setEstadoFilter("todas");
    setCategoriaFilter("todas");
    setPilarFilter("todos");
    setRegionFilter("todos");
    setResponsableFilter("todos");
    setFechaRango({ desde: null, hasta: null });
    setOrdenFilter("score");
    setPage(1);
  };

  const hasFilters =
    search ||
    estadoFilter !== "todas" ||
    categoriaFilter !== "todas" ||
    pilarFilter !== "todos" ||
    regionFilter !== "todos" ||
    responsableFilter !== "todos" ||
    fechaRango.desde ||
    fechaRango.hasta ||
    ordenFilter !== "score";

  return (
    <div className="space-y-6" data-testid="licitaciones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display theme-text flex items-center gap-2">
            <FileSearch className="w-7 h-7 text-green-400" />
            Licitaciones Abiertas
          </h1>
          <p className="theme-text-secondary text-sm">
            Licitaciones PLACSP donde SRS puede licitar directamente
          </p>
        </div>
        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-slate-950/50 border border-slate-800 rounded-lg p-1">
          <Button
            variant={viewMode === "table" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("table")}
            className={`h-8 px-3 gap-1.5 ${viewMode === "table" ? "bg-green-500/20 text-green-400" : "text-slate-400"}`}
          >
            <LayoutList className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Tabla</span>
          </Button>
          <Button
            variant={viewMode === "kanban" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("kanban")}
            className={`h-8 px-3 gap-1.5 ${viewMode === "kanban" ? "bg-green-500/20 text-green-400" : "text-slate-400"}`}
          >
            <Columns3 className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Kanban</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="theme-bg-secondary border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs theme-text-muted uppercase tracking-wide">Total</p>
                  <p className="text-2xl font-bold theme-text">{stats.total}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <FileSearch className="w-5 h-5 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="theme-bg-secondary border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs theme-text-muted uppercase tracking-wide">Alta Prioridad</p>
                  <p className="text-2xl font-bold text-green-400">
                    {stats.por_categoria?.alta_prioridad || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="theme-bg-secondary border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs theme-text-muted uppercase tracking-wide">Revisar</p>
                  <p className="text-2xl font-bold text-yellow-400">
                    {stats.por_categoria?.revisar || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="theme-bg-secondary border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs theme-text-muted uppercase tracking-wide">Vencen 7d</p>
                  <p className="text-2xl font-bold text-red-400">
                    {stats.proximas_vencer_7d || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Timer className="w-5 h-5 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="theme-bg-secondary border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-6 items-center">
            {/* Left: Count Box */}
            <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-4 min-w-[140px] flex flex-col justify-center items-center text-center shadow-sm">
              <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-1">
                LICITACIONES
              </p>
              <p className="text-3xl font-bold text-green-400 leading-none">{totalCount}</p>
            </div>

            {/* Right: Filters Grid */}
            <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-3 items-center">
              {/* Search */}
              <div className="relative w-full">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                <Input
                  placeholder="Buscar por expediente, objeto, organo..."
                  className="pl-9 bg-slate-950 border-slate-800 focus:border-green-500/50 transition-colors h-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Estado - hide in kanban mode */}
              {viewMode === "table" ? (
                <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 h-10">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="todas">Todos los estados</SelectItem>
                    <SelectItem value="nueva">Nueva</SelectItem>
                    <SelectItem value="en_estudio">En Estudio</SelectItem>
                    <SelectItem value="viable">Viable</SelectItem>
                    <SelectItem value="preparando_oferta">Preparando Oferta</SelectItem>
                    <SelectItem value="oferta_presentada">Presentada</SelectItem>
                    <SelectItem value="descartada">Descartada</SelectItem>
                    <SelectItem value="expirada">Expirada</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="hidden lg:block" />
              )}

              {/* Categoria */}
              <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
                <SelectTrigger className="bg-slate-950 border-slate-800 h-10">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="alta_prioridad">Alta Prioridad</SelectItem>
                  <SelectItem value="revisar">Revisar</SelectItem>
                  <SelectItem value="baja_prioridad">Baja Prioridad</SelectItem>
                </SelectContent>
              </Select>

              {/* Pilar */}
              <Select value={pilarFilter} onValueChange={setPilarFilter}>
                <SelectTrigger className="bg-slate-950 border-slate-800 h-10">
                  <SelectValue placeholder="Pilar" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="todos">Todos los pilares</SelectItem>
                  {stats?.por_pilar &&
                    Object.entries(stats.por_pilar)
                      .filter(([key]) => key !== "NO_CLASIFICADO")
                      .sort(([, a], [, b]) => b - a)
                      .map(([pilar, count]) => (
                        <SelectItem key={pilar} value={pilar}>
                          {formatPilarName(pilar)} ({count})
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>

              {/* Región */}
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="bg-slate-950 border-slate-800 h-10">
                  <MapPin className="w-3.5 h-3.5 mr-1 text-slate-500" />
                  <SelectValue placeholder="Región" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                  <SelectItem value="todos">Todas regiones</SelectItem>
                  {stats?.por_region &&
                    Object.entries(stats.por_region)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([region, count]) => (
                        <SelectItem key={region} value={region}>
                          {region} ({count})
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>

              {/* Asignado */}
              <Select value={responsableFilter} onValueChange={setResponsableFilter}>
                <SelectTrigger className="bg-slate-950 border-slate-800 h-10">
                  <SelectValue placeholder="Asignado" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="sin_asignar">Sin asignar</SelectItem>
                  {users.map((u, idx) => {
                    const color = userColors[idx % userColors.length];
                    return (
                      <SelectItem key={u.user_id} value={u.name}>
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-5 h-5 rounded-full border ${color.border} bg-slate-900/50 flex items-center justify-center ${color.text} text-[9px] font-bold`}
                          >
                            {getInitials(u.name)}
                          </div>
                          <span>{u.name}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              {/* v4.4.33 — Filtro fecha (plazo presentación) */}
              <DateRangeFilter
                value={fechaRango}
                onChange={setFechaRango}
                label="Plazo"
                compact
                presets={PRESETS_PLAZO}
              />

              {/* Clear — solo visible con filtros activos */}
              {hasFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="h-10 px-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors text-xs font-medium gap-1.5"
                >
                  <XCircle className="w-4 h-4" />
                  Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* TABLE VIEW */}
      {/* ============================================================ */}
      {viewMode === "table" && (
        <Card className="theme-bg-secondary border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400 w-[70px]">Score</TableHead>
                  <TableHead className="text-slate-400 w-[160px] hidden md:table-cell">Expediente</TableHead>
                  <TableHead className="text-slate-400">Objeto</TableHead>
                  <TableHead className="text-slate-400 text-right">Presupuesto</TableHead>
                  <TableHead className="text-slate-400 hidden lg:table-cell">Pilar</TableHead>
                  <TableHead className="text-slate-400 hidden lg:table-cell">Región</TableHead>
                  <TableHead className="text-slate-400">Estado</TableHead>
                  <TableHead className="text-slate-400 hidden md:table-cell">Asignado</TableHead>
                  <TableHead className="text-slate-400 text-center">Plazo</TableHead>
                  <TableHead className="text-slate-400 w-[50px] hidden md:table-cell"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-green-400" />
                      <p className="mt-2 theme-text-muted">Cargando licitaciones...</p>
                    </TableCell>
                  </TableRow>
                ) : licitaciones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12">
                      <FileSearch className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                      <p className="theme-text-muted">No se encontraron licitaciones</p>
                      {hasFilters && (
                        <Button variant="link" onClick={clearFilters} className="text-green-400 mt-2">
                          Limpiar filtros
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  licitaciones.map((lic) => {
                    const catConfig = getCategoriaConfig(lic.categoria);
                    const estado = estadoConfig[lic.estado] || estadoConfig.nueva;
                    const EstadoIcon = estado.icon;
                    const dias = getDiasRestantes(lic.fecha_limite_presentacion);
                    const diasColor = getDiasColor(dias);

                    return (
                      <TableRow
                        key={lic._id}
                        id={`lic-row-${lic._id}`}
                        className={`border-slate-800 hover:bg-slate-800/50 cursor-pointer transition-all duration-500 ${
                          highlightId === lic._id
                            ? "bg-cyan-500/15 ring-1 ring-cyan-500/50 animate-pulse"
                            : ""
                        }`}
                        onClick={() => openModal(lic)}
                      >
                        {/* Score */}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-8 rounded-full ${catConfig.color}`} />
                            <span className={`font-bold ${catConfig.textColor}`}>
                              {lic.score_total}
                            </span>
                          </div>
                        </TableCell>

                        {/* Expediente */}
                        <TableCell className="hidden md:table-cell">
                          {lic.expediente_id ? (
                            <div>
                              <p className="font-mono text-xs text-slate-300 font-medium">
                                {lic.expediente_id}
                              </p>
                              <p className="font-mono text-[10px] text-slate-500">
                                PLACSP: {lic.expediente}
                              </p>
                            </div>
                          ) : (
                            <span className="font-mono text-xs text-slate-400">
                              {lic.expediente?.slice(-15) || "-"}
                            </span>
                          )}
                        </TableCell>

                        {/* Objeto */}
                        <TableCell>
                          <div>
                            <p className="font-medium theme-text truncate max-w-[150px] sm:max-w-[300px]">
                              {lic.objeto_contrato}
                            </p>
                            <p className="text-xs theme-text-muted truncate max-w-[150px] sm:max-w-[300px]">
                              {lic.organo_contratacion}
                            </p>
                          </div>
                        </TableCell>

                        {/* Presupuesto */}
                        <TableCell className="text-right font-semibold text-green-400">
                          {formatCurrency(lic.presupuesto_base)}
                        </TableCell>

                        {/* Pilar */}
                        <TableCell className="hidden lg:table-cell">
                          {lic.pilar_principal && lic.pilar_principal !== "NO_CLASIFICADO" ? (
                            <Badge
                              variant="outline"
                              className="border-green-500/30 text-green-400 text-xs"
                            >
                              {lic.pilar_principal.split(" / ")[0]}
                            </Badge>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </TableCell>

                        {/* Región */}
                        <TableCell className="hidden lg:table-cell">
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-slate-500" />
                            {lic.lugar_ejecucion || "-"}
                          </span>
                        </TableCell>

                        {/* Estado */}
                        <TableCell>
                          <Badge
                            className={`${estado.bg} ${estado.color} border-0 cursor-pointer hover:opacity-80 transition-opacity`}
                            onClick={(e) => cycleEstado(e, lic)}
                            title="Click para cambiar estado"
                          >
                            <EstadoIcon className="w-3 h-3 mr-1" />
                            {estado.label}
                          </Badge>
                        </TableCell>

                        {/* Asignado - Círculo con iniciales */}
                        <TableCell className="hidden md:table-cell" onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={lic.responsable || "none"}
                            onValueChange={(v) =>
                              assignResponsable(
                                { stopPropagation: () => {} },
                                lic,
                                v === "none" ? null : v
                              )
                            }
                          >
                            <SelectTrigger className="w-10 h-10 rounded-full p-0 border-0 bg-transparent hover:bg-slate-700/30 transition-colors flex items-center justify-center">
                              {lic.responsable ? (
                                <div
                                  className={`w-9 h-9 rounded-full border-2 ${
                                    getUserColorByName(lic.responsable, users)?.border || "border-slate-500"
                                  } bg-slate-900/50 flex items-center justify-center ${
                                    getUserColorByName(lic.responsable, users)?.text || "text-slate-400"
                                  } text-sm font-bold`}
                                >
                                  {getInitials(lic.responsable)}
                                </div>
                              ) : (
                                <div className="w-9 h-9 rounded-full border-2 border-slate-600 bg-slate-900/50 flex items-center justify-center text-slate-500">
                                  <User className="w-4 h-4" />
                                </div>
                              )}
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-700">
                              <SelectItem value="none" className="text-slate-400">
                                <div className="flex items-center gap-2">
                                  <XCircle className="w-4 h-4 text-red-400" />
                                  <span>Sin asignar</span>
                                </div>
                              </SelectItem>
                              {users.map((u, idx) => {
                                const color = userColors[idx % userColors.length];
                                return (
                                  <SelectItem key={u.user_id} value={u.name}>
                                    <div className="flex items-center gap-2">
                                      <div
                                        className={`w-7 h-7 rounded-full border-2 ${color.border} bg-slate-900/50 flex items-center justify-center ${color.text} text-xs font-bold`}
                                      >
                                        {getInitials(u.name)}
                                      </div>
                                      <span className="text-slate-300">{u.name}</span>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </TableCell>

                        {/* Plazo */}
                        <TableCell className="text-center">
                          {dias !== null ? (
                            <span className={`font-bold ${diasColor}`}>
                              {dias <= 0 ? "Vencida" : `${dias}d`}
                            </span>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </TableCell>

                        {/* Link */}
                        <TableCell className="hidden md:table-cell">
                          {lic.url_licitacion && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-green-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(lic.url_licitacion, "_blank");
                              }}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
              <p className="text-sm text-slate-400">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} de{" "}
                <span className="font-semibold text-slate-200">
                  {totalCount.toLocaleString("es-ES")}
                </span>
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-white disabled:opacity-30"
                  disabled={page <= 1}
                  onClick={() => setPage(1)}
                >
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-white disabled:opacity-30"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 text-sm text-slate-300">
                  Pag. <span className="font-semibold text-white">{page}</span> / {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-white disabled:opacity-30"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-white disabled:opacity-30"
                  disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                >
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* ============================================================ */}
      {/* KANBAN VIEW */}
      {/* ============================================================ */}
      {viewMode === "kanban" && (
        <DragDropContext onDragEnd={handleDragEnd}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-6 h-6 animate-spin text-green-400" />
              <p className="ml-3 theme-text-muted">Cargando licitaciones...</p>
            </div>
          ) : (
            <div className="grid pb-4 gap-2" style={{ minHeight: "calc(100vh - 420px)", gridTemplateColumns: "repeat(6, minmax(0, 1fr)) 160px" }}>
              {/* Active columns */}
              {KANBAN_COLUMNS.map((col) => {
                const colLics = licitaciones.filter((l) => l.estado === col.id);
                const EstadoIcon = estadoConfig[col.id]?.icon;

                return (
                  <div key={col.id} className="min-w-0 flex flex-col">
                    {/* Column Header */}
                    <div
                      className={`rounded-t-xl p-3 ${col.bgClass}`}
                      style={{ borderTop: `3px solid ${col.borderColor}` }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {EstadoIcon && <EstadoIcon className="w-4 h-4 theme-text" />}
                          <span className="font-semibold theme-text text-sm">{col.label}</span>
                        </div>
                        <Badge variant="secondary" className="theme-bg-secondary theme-text text-xs">
                          {colLics.length}
                        </Badge>
                      </div>
                    </div>

                    {/* Droppable Area */}
                    <Droppable droppableId={col.id}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`flex-1 bg-slate-950/30 rounded-b-xl p-2 border border-slate-800 border-t-0 transition-colors ${
                            snapshot.isDraggingOver ? "ring-2 ring-green-500/30 bg-green-500/5" : ""
                          }`}
                          style={{ minHeight: "150px" }}
                        >
                          {colLics.length === 0 && !snapshot.isDraggingOver && (
                            <div className="text-center py-8 text-slate-600 text-xs">
                              Sin licitaciones
                            </div>
                          )}
                          {colLics.map((lic, idx) => {
                            const catConfig = getCategoriaConfig(lic.categoria);
                            const dias = getDiasRestantes(lic.fecha_limite_presentacion);

                            return (
                              <Draggable key={lic._id} draggableId={lic._id} index={idx}>
                                {(prov, snap) => (
                                  <div
                                    ref={prov.innerRef}
                                    {...prov.draggableProps}
                                    className="mb-2"
                                  >
                                    <Card
                                      className={`p-3 cursor-pointer theme-bg-secondary border-slate-800 hover:border-green-500/30 transition-all group ${
                                        snap.isDragging ? "shadow-xl ring-2 ring-green-500/30" : ""
                                      }`}
                                      onClick={() => openModal(lic)}
                                    >
                                      {/* Drag handle + Score + Expediente */}
                                      <div className="flex items-center gap-1.5 mb-1.5">
                                        <div
                                          {...prov.dragHandleProps}
                                          className="theme-text-muted opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
                                        >
                                          <GripVertical className="w-3 h-3" />
                                        </div>
                                        <div className={`w-1.5 h-5 rounded-full ${catConfig.color}`} />
                                        <span className={`font-bold text-sm ${catConfig.textColor}`}>
                                          {lic.score_total}
                                        </span>
                                        <span className="font-mono text-[10px] text-slate-500 ml-auto truncate max-w-[100px]" title={lic.expediente_id || lic.expediente}>
                                          {lic.expediente_id || lic.expediente?.slice(-10)}
                                        </span>
                                      </div>

                                      {/* Objeto truncated */}
                                      <p className="text-xs theme-text line-clamp-2 mb-2 leading-relaxed">
                                        {lic.objeto_contrato}
                                      </p>

                                      {/* Bottom row */}
                                      <div className="flex items-center justify-between text-[10px]">
                                        <span className="text-green-400 font-semibold">
                                          {formatCurrency(lic.presupuesto_base)}
                                        </span>
                                        {dias !== null && (
                                          <span className={`font-bold ${getDiasColor(dias)}`}>
                                            {dias <= 0 ? "Vencida" : `${dias}d`}
                                          </span>
                                        )}
                                      </div>

                                      {/* Pilar + Responsable row */}
                                      <div className="flex items-center justify-between mt-1.5">
                                        {lic.pilar_principal &&
                                          lic.pilar_principal !== "NO_CLASIFICADO" ? (
                                            <Badge
                                              variant="outline"
                                              className="border-green-500/30 text-green-400 text-[10px] py-0"
                                            >
                                              {formatPilarName(lic.pilar_principal.split(" / ")[0])}
                                            </Badge>
                                          ) : (
                                            <span />
                                          )}
                                        {lic.responsable && (
                                          <div
                                            className={`w-6 h-6 rounded-full border-2 ${
                                              getUserColorByName(lic.responsable, users)?.border || "border-slate-500"
                                            } bg-slate-900/50 flex items-center justify-center ${
                                              getUserColorByName(lic.responsable, users)?.text || "text-slate-400"
                                            } text-[9px] font-bold`}
                                            title={lic.responsable}
                                          >
                                            {getInitials(lic.responsable)}
                                          </div>
                                        )}
                                      </div>
                                    </Card>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}

              {/* Terminal states */}
              <div className="min-w-0 flex flex-col">
                <div className="rounded-xl p-3 bg-slate-800/30 border border-slate-800">
                  <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-3">
                    Finalizadas
                  </p>
                  {TERMINAL_STATES.map((ts) => {
                    const cfg = estadoConfig[ts];
                    const count = licitaciones.filter((l) => l.estado === ts).length;
                    const Icon = cfg?.icon;
                    return (
                      <div
                        key={ts}
                        className="flex items-center justify-between py-1.5"
                      >
                        <div className="flex items-center gap-2">
                          {Icon && <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />}
                          <span className={`text-xs ${cfg.color}`}>{cfg.label}</span>
                        </div>
                        <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400">
                          {count}
                        </Badge>
                      </div>
                    );
                  })}
                  {licitaciones.filter((l) => TERMINAL_STATES.includes(l.estado)).length === 0 && (
                    <p className="text-xs text-slate-600 text-center py-2">Ninguna</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DragDropContext>
      )}

      {/* Modal */}
      <LicitacionModal
        open={modalOpen}
        onClose={closeModal}
        licitacion={selectedLicitacion}
        user={user}
      />
    </div>
  );
}
